import type { RichText } from "@storytell/cp-client";
import { createPromptEditor, proseMirrorToRichText, useMentionsState } from "@storytell/rich-text";
import type { Asset } from "@storytell/types";
import type { PmDoc, PmNode } from "@storytell/types/richText";
import { PmNodeType } from "@storytell/types/richText";
import { type ParentComponent, createContext, createSignal, useContext } from "solid-js";
import { useGateActionByChatPermission } from "~/hooks/useGateActionByChatPermission";
import { useWire } from "~/wire";
import type { PromptBarContextType, PromptBarProps } from "../PromptBar.types";
import { useAutoImprove } from "./components/PromptAutoImprove";
import { VariableV1EditorRenderer } from "./components/VariableV1Renderer/VariableV1Renderer";
import { useCreditsDisplay, useKnowledgeSourcesDisplay, usePromptInput, usePromptMode } from "./hooks";

const PromptBarContext = createContext<PromptBarContextType>();

export const PromptBarProvider: ParentComponent<PromptBarProps> = (props) => {
  const input = usePromptInput();
  const mode = usePromptMode();
  const credits = useCreditsDisplay(props.streamingState?.accumulatedMetrics);
  const knowledgeSources = useKnowledgeSourcesDisplay();
  const wire = useWire();

  const handleSubmit = (richText: RichText.RichText, text: string) => {
    props.onSubmit({
      richText,
      text,
      model: knowledgeSources.selectedModel(),
    });
  };
  const filterAsset = (asset: Asset) =>
    !["TOO_MANY_REQUESTS", "UNSPECIFIED", "ARCHIVED", "FAILED"].includes(asset.status.state);

  const mentionsState = useMentionsState({
    // TODO: @andi: limit how many we're returning here
    getMentionItems: async (query) => {
      return [
        ...wire.views
          .search(query || "")
          .map((view) => ({ type: "view" as const, id: view.id, data: view, text: view.name })),
        ...wire.labels
          .search(query || "")
          .map((label) => ({ type: "label" as const, id: label.id, data: label, text: label.name })),
        ...wire.concepts
          .search(query || "")
          .map((concept) => ({ type: "concept" as const, id: concept.id, data: concept, text: concept.name })),
        ...wire.assets
          .search(query || "")
          .filter(filterAsset)
          .map((asset) => ({ type: "asset" as const, id: asset.id, data: asset, text: asset.filename })),
      ];
    },
  });

  const onSubmit = () => {
    const editor = promptEditor.editor();
    if (!editor) return;
    const doc = editor.getJSON() as PmDoc | undefined;
    if (doc) {
      const richText = proseMirrorToRichText(doc);
      onSubmitWithContent(richText, editor.getText() ?? "");
    }
  };
  const onSubmitWithContent = (richText: RichText.RichText, text: string) => {
    const suggestion = autoImprove.improveSuggestion();
    if (!autoImprove.improveLoading() && !!suggestion && autoImprove.improveState() === "auto") {
      handleSubmit(proseMirrorToRichText(suggestion), autoImprove.improveSuggestionRaw() || "");
      return;
    }

    const quote = props.quote?.();
    if (quote && quote.trim()) {
      const editor = promptEditor.editor();
      if (!editor) return;

      const userDoc = editor.getJSON() as PmDoc | undefined;
      const userContent = userDoc?.content || [];

      const combinedContent: PmNode[] = [
        {
          type: PmNodeType.Blockquote,
          content: [
            {
              type: PmNodeType.Paragraph,
              content: [
                {
                  type: PmNodeType.Text,
                  text: quote,
                },
              ],
            },
          ],
        },
        {
          type: PmNodeType.Paragraph,
          content: [],
        },
        ...userContent,
      ];

      const combinedDoc: PmDoc = {
        type: PmNodeType.Doc,
        content: combinedContent,
      };

      const combinedRichText = proseMirrorToRichText(combinedDoc);
      const combinedText = `${quote}\n\n${text}`;

      handleSubmit(combinedRichText, combinedText);
      return;
    }

    handleSubmit(richText, text);
  };
  const promptEditor = createPromptEditor({
    autofocus: () => true,
    mentionsState,
    variableV1Props: {
      renderer: VariableV1EditorRenderer,
    },
    placeholder: () => "What are you trying to accomplish?",
    onSubmit: onSubmitWithContent,
    onInput: (text) => {
      input.setInputValue(text);
    },
  });
  const autoImprove = useAutoImprove({ ctx: promptEditor });

  const contextValue: PromptBarContextType = {
    input: {
      inputValue: input.inputValue,
      setInputValue: input.setInputValue,
      characterCount: input.characterCount,
      lineCount: input.lineCount,
      hasInputText: input.hasInputText,
    },
    prompt: promptEditor,
    autoImprove,
    mode: {
      selectedMode: mode.selectedMode,
      setSelectedMode: mode.setSelectedMode,
      placeholderText: mode.placeholderText,
    },
    credits: {
      showTooltip: credits.showTooltip,
      showPricingModal: credits.showPricingModal,
      setShowTooltip: credits.setShowTooltip,
      toggleTooltip: credits.toggleTooltip,
      hideTooltip: credits.hideTooltip,
      openPricingModal: credits.openPricingModal,
      closePricingModal: credits.closePricingModal,
      credits: credits.credits,
      dailyCredits: credits.dailyCredits,
      planType: credits.planType,
      totalCredits: credits.totalCredits,
    },
    knowledgeSources: {
      showTooltip: knowledgeSources.showTooltip,
      training: knowledgeSources.training,
      web: knowledgeSources.web,
      myKnowledge: knowledgeSources.myKnowledge,
      memories: knowledgeSources.memories,
      selectedModel: knowledgeSources.selectedModel,
      setShowTooltip: knowledgeSources.setShowTooltip,
      toggleTooltip: knowledgeSources.toggleTooltip,
      hideTooltip: knowledgeSources.hideTooltip,
      toggleTraining: knowledgeSources.toggleTraining,
      toggleWeb: knowledgeSources.toggleWeb,
      toggleMyKnowledge: knowledgeSources.toggleMyKnowledge,
      toggleMemories: knowledgeSources.toggleMemories,
      setSelectedModel: knowledgeSources.setSelectedModel,
    },
    submission: {
      handleSubmit: onSubmit,
      handleSubmitWithContent: onSubmitWithContent,
    },
    streaming: props.streamingState
      ? {
          ...props.streamingState,
          onStopStreaming: props.onStopStreaming,
        }
      : undefined,
  };

  return <PromptBarContext.Provider value={contextValue}>{props.children}</PromptBarContext.Provider>;
};

export const usePromptBarContext = (): PromptBarContextType => {
  const context = useContext(PromptBarContext);
  if (!context) {
    throw new Error("usePromptBarContext must be used within a PromptBarProvider");
  }
  return context;
};
